import React, { useState } from "react"
import { DateTime } from "luxon"
import { makeStyles } from "tss-react/mui"
import { ToggleButton, ToggleButtonGroup, Typography, FormHelperText, Stack, Tooltip, Skeleton } from "@mui/material"
import { useApiQuery } from "../../../../../../../core/api/useApiQuery"
import { UserWeekMenu } from "../../../weekMenuApi"
import { MenuMeeting } from "../../../weekMenuTypes"
import { LocalizedStrict } from "../../../../../../localization/components/AppLocalized"
import { StandardLoadingButton } from "../../../../../../../shared/components/buttons/LoadingButton"
import { useApiMutation } from "../../../../../../../core/api/useApiMutation"
import { useDateTime } from "../../../../../../localization/useDateTime"
import { fetchOrderFreeBusySchedule, ordersExtendBooking } from "../../../meetingServiceApi"
import { StandardTimePicker } from "../../../../../../../shared/components/pickers/StandardTimePicker"

type TimeOption = {
    label: string
    minutes: number
    slots: number
}

type AvailabilityMap = {
    label: string
    disabled: boolean
    unavailableResources: string[]
}

type ExtendBookingContentParams = {
    meeting: MenuMeeting
    onConfirm: () => void
}

const useStyles = makeStyles()(() => ({
    toggleGroup: { display: "flex", flexWrap: "wrap", justifyContent: "center" },
    toggleButton: {
        width: "5rem",
        borderRadius: ".25rem !important",
        border: "1px solid rgba(0, 0, 0, 0.12) !important",
        textTransform: "none",
        "&:disabled": {
            pointerEvents: "auto",
        },
        "&.Mui-selected": {
            backgroundColor: "#4CAF50",
            color: "white",
            border: "1px solid #4CAF50 !important",
            "&:hover": { backgroundColor: "#45a049" },
        },
        "&.Mui-disabled": {
            opacity: 0.5,
            cursor: "not-allowed",
        },
    },
    skeletonCard: {
        width: "100%",
        height: "2rem",
        borderRadius: ".625rem",
    },
    confirmButton: {
        marginBottom: "2rem !important",
    },
}))

function diffInMinutesSameDay(start: DateTime, end: DateTime): number {
    const startMinutes = start.hour * 60 + start.minute
    const endMinutes = end.hour * 60 + end.minute
    return endMinutes - startMinutes
}

const timeOptions: TimeOption[] = [
    { label: "15m", minutes: 15, slots: 3 },
    { label: "30m", minutes: 30, slots: 6 },
    { label: "45m", minutes: 45, slots: 9 },
    { label: "60m", minutes: 60, slots: 12 },
]

const OTHER_OPTION = "Other"

export function ExtendBookingContent({ meeting, onConfirm }: ExtendBookingContentParams) {
    const dateTimeFactory = useDateTime()
    const { classes } = useStyles()
    const [selectedTime, setSelectedTime] = useState<string>("15m")
    const endTime = dateTimeFactory.fromApi(meeting?.meetingDetails?.endDateTime)

    const [customEndTime, setCustomEndTime] = useState<DateTime>(endTime)

    const { data: response, isLoading } = useApiQuery(fetchOrderFreeBusySchedule, {
        queryName: UserWeekMenu.USER_MEETING_ORDER_FREE_BUSY_SCHEDULE,
        dependencies: {
            orderId: meeting.id,
            availabilityViewInterval: 5,
            startTime: dateTimeFactory.toApiISOMinutesFormat(
                dateTimeFactory.fromApi(meeting.meetingDetails.endDateTime),
            ),
            endTime: dateTimeFactory.toApiISOMinutesFormat(
                dateTimeFactory.fromApi(meeting.meetingDetails.endDateTime).plus({ minute: 60 }),
            ),
        },
    })

    const { mutate: callExtendBooking, isLoading: isExtending } = useApiMutation(ordersExtendBooking, {
        async onSuccess(_, __, context) {
            await context.queryClient.invalidateQueries({
                queryKey: [UserWeekMenu.MEETING_SERVICE_LIST],
            })
            await context.queryClient.invalidateQueries({
                queryKey: [UserWeekMenu.USER_MEETING_ORDER_FREE_BUSY_SCHEDULE],
            })
            onConfirm()
        },
    })

    const interval = response?.availabilityDetails.availabilityViewInterval

    // Determine maximum available time across all resources
    const maxAvailableSlots =
        response?.resources &&
        Math.min(
            ...response?.resources?.map((resource) => {
                const availabilityView = resource.availability.availabilityView
                let consecutiveSlots = 0
                for (let i = 0; i < availabilityView.length; i++) {
                    if (availabilityView[i] === "0") {
                        consecutiveSlots++
                    } else {
                        break
                    }
                }
                return consecutiveSlots
            }),
        )
    const maxAvailableMinutes = (maxAvailableSlots || 0) * (interval || 0)

    // Check availability for each time option
    const availabilityMap: AvailabilityMap[] = timeOptions.map((option) => {
        const requiredSlots = option.slots
        let unavailableResources: string[] = []

        response?.resources.forEach((resource) => {
            const availabilityView = resource.availability.availabilityView
            const isAvailable = availabilityView
                .slice(0, requiredSlots)
                .split("")
                .every((slot) => slot === "0")

            if (!isAvailable) {
                unavailableResources.push(resource.resource.name)
            }
        })

        return {
            label: option.label,
            disabled: unavailableResources.length > 0,
            unavailableResources,
        }
    })

    const handleTimeChange = (_event: React.MouseEvent<HTMLElement>, newTime: string | null) => {
        if (newTime !== null) {
            setSelectedTime(newTime)
            setCustomEndTime(endTime)
        }
    }

    // Selected minutes calculation
    let selectedMinutes = 0
    if (selectedTime === OTHER_OPTION && customEndTime && endTime) {
        selectedMinutes = diffInMinutesSameDay(endTime, customEndTime)
    } else {
        selectedMinutes = timeOptions.find((opt) => opt.label === selectedTime)?.minutes || 0
    }

    const isCustomTimeValid =
        selectedTime !== OTHER_OPTION || (selectedMinutes > 0 && selectedMinutes <= maxAvailableMinutes)

    const newEndTime =
        endTime && selectedMinutes > 0
            ? dateTimeFactory.toHourMinute(endTime.plus({ minutes: selectedMinutes }))
            : undefined

    const handleConfirm = () => {
        callExtendBooking({
            orderId: meeting.id,
            endDateTime: (endTime && endTime.plus({ minutes: selectedMinutes }).toISO()) || "",
        })
    }

    return (
        <Stack direction="column" alignItems="center" spacing="3rem">
            <Stack direction="column" alignItems="center" spacing="3rem">
                <ToggleButtonGroup
                    value={selectedTime}
                    exclusive
                    onChange={handleTimeChange}
                    className={classes.toggleGroup}
                    sx={{ gap: 1 }}
                >
                    {[...availabilityMap].map(({ label, disabled, unavailableResources }) => (
                        <ToggleButton key={label} value={label} className={classes.toggleButton} disabled={disabled}>
                            <Tooltip
                                key={label}
                                title={disabled ? `Unavailable for: ${unavailableResources.join(", ")}` : ""}
                                placement="bottom"
                            >
                                <span>{label}</span>
                            </Tooltip>
                        </ToggleButton>
                    ))}
                    <ToggleButton key={OTHER_OPTION} value={OTHER_OPTION} className={classes.toggleButton}>
                        <LocalizedStrict id="user-home-canteen-upcoming-orders-extend-other-button">
                            <>Other</>
                        </LocalizedStrict>
                    </ToggleButton>
                </ToggleButtonGroup>
                {selectedTime === OTHER_OPTION && endTime && (
                    <Stack alignItems="center" width="fit-content" spacing=".5rem">
                        <LocalizedStrict
                            id="user-home-canteen-upcoming-orders-extend-input-custom-time"
                            attrs={{ label: true }}
                        >
                            <StandardTimePicker
                                label="Select end time:"
                                dateTime={customEndTime ?? endTime}
                                setDateTime={setCustomEndTime}
                                minTime={endTime.plus({ minutes: 1 })}
                                isMinutesStep={false}
                            />
                        </LocalizedStrict>
                        {customEndTime && selectedMinutes > maxAvailableMinutes && (
                            <LocalizedStrict
                                id="user-home-canteen-upcoming-orders-extend-modal-max-error"
                                vars={{ maxAvailableMinutes }}
                            >
                                <FormHelperText error>
                                    Maximum available time is {maxAvailableMinutes} minutes.
                                </FormHelperText>
                            </LocalizedStrict>
                        )}
                    </Stack>
                )}
            </Stack>
            {isLoading ? (
                <Skeleton variant="rectangular" className={classes.skeletonCard} />
            ) : (
                <LocalizedStrict
                    id="user-home-canteen-upcoming-orders-extend-modal-subtitle"
                    vars={{
                        meetingName: meeting.meetingDetails.meetingName,
                        meetingTime: newEndTime ?? "",
                    }}
                >
                    <Typography variant="body1" textAlign="center">
                        Extend booking?
                    </Typography>
                </LocalizedStrict>
            )}
            <LocalizedStrict id="user-home-canteen-upcoming-orders-end-modal-confirm">
                <StandardLoadingButton
                    className={classes.confirmButton}
                    fullWidth={false}
                    disabled={!isCustomTimeValid || selectedMinutes === 0}
                    loading={isExtending}
                    onClick={handleConfirm}
                >
                    Confirm
                </StandardLoadingButton>
            </LocalizedStrict>
        </Stack>
    )
}
